import type {
    SeriesOptionsType,
    PointOptionsObject,
    StackItemObject,
} from 'highcharts';
import { UIChartColumn } from '../../components/column/ui-chart-column';
import {
    element,
    whenDOMReady,
} from '../../../shared-components/global/ui-helpers';
import type { UIChart } from '../../components/chart/ui-chart';
import type { ComponentPlugin } from '../../../shared-components/components/ui-element.type';

interface StackIconOptions {
    'stack-icon'?: string;
    'stack-icon-color'?: string;
    'stack-icon-id'?: string;
}

type SeriesOptionsWithStackIcon = SeriesOptionsType & StackIconOptions;
type PointOptionsWithStackIcon = PointOptionsObject & StackIconOptions;

/**
 * @class
 * @classdesc Class for ui-chart-column stack-labels.
 * @alias UIChartColumnStackLabels
 * @implements {ComponentPlugin}
 * @param {HTMLElement} target
 * @classdesc Plugin for UIChartColumn which set icon to the stack label on the top.
 * Works with 'ui-point'/'ui-series' that have attribute
 * 'stack-icon'/'stack-icon-color'/'stack-icon-id'(ids only with 'ui-point').
 * NB: icons can be set only in the first series item.
 * @example
 * <ui-chart-column>
 *   <ui-series name="gain" class="highcharts-color-13">
 *     <ui-point y="5.61" stack-icon="food" stack-icon-color="orange"></ui-point>
 *     <ui-point y="8.22"></ui-point>
 *     <ui-point y="8.22" stack-icon="envelope"></ui-point>
 *   </ui-series>
 *   <ui-series name="payments" class="highcharts-color-2">
 *     <ui-point y="123.05"></ui-point>
 *     <ui-point y="80.22"></ui-point>
 *     <ui-point y="80.22"></ui-point>
 *   </ui-series>
 * </ui-chart-column>
 */
export class UIChartColumnStackLabels implements ComponentPlugin {
    constructor(private readonly target: UIChartColumn) {
        this.target = target;
    }

    /**
     * Builds icon on the top of the stack
     * @param {UIChart} current
     * @param {number} x
     * @returns {string | undefined}
     */
    buildIconedStackLabel(current: UIChart, x: number): string {
        const firstSeries = current.chart?.series?.[0];

        if (!firstSeries) {
            return '';
        }

        const seriesOptions: SeriesOptionsWithStackIcon =
            firstSeries.userOptions;
        const pointOptions: PointOptionsWithStackIcon =
            firstSeries.points?.[x]?.options;

        const isSeriesWithIcon = seriesOptions?.['stack-icon'];
        const isPointWithIcon = pointOptions?.['stack-icon'];
        let options: StackIconOptions;

        if (isSeriesWithIcon) {
            options = seriesOptions;
        } else if (isPointWithIcon) {
            options = pointOptions!;
        } else {
            return '';
        }

        return element`
        <ui-icon
          glyph="${options['stack-icon']}"
          color="${options['stack-icon-color'] || null}"
          id="${options['stack-icon-id'] || null}"></ui-icon>
    `.outerHTML;
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        const chart = this.target;

        // Needed because of async ui-chart drawing
        whenDOMReady(() => {
            this.target.chart?.update({
                yAxis: {
                    stackLabels: {
                        enabled: true,
                        useHTML: true,
                        formatter: function (this: StackItemObject): string {
                            const x = this.x;
                            return UIChartColumnStackLabels.prototype.buildIconedStackLabel.call(
                                null,
                                chart,
                                x
                            );
                        },
                    },
                },
            });
        }, false);
    }
}

UIChartColumn.registerPlugin('stack-labels', UIChartColumnStackLabels);
